import {observer} from 'mobx-react-lite'
import React from 'react'
import {Lang} from 'yougile-lang'
import {LinkButton} from '@components/common/base/buttons'
import {CheckboxNew} from '@components/common/base/checkbox-new/checkbox-new'
import {ItemName} from '@components/common/sandbox/list-view/components/item-name'
import {LiteItemRow} from '@components/common/sandbox/list-view/components/lite-item-row'
import {useListStore} from '@components/common/sandbox/list-view/list-store'
import * as Tw from '@components/common/sandbox/list-view/tw-components'
import {
  TwCell,
  TwCellFunctions,
  TwCellFunctionsCompensator,
  TwInnerCell,
  TwRowContainer,
  TwRowShell
} from '@components/common/sandbox/list-view/tw-components'
import {AddMode, NullableId} from '@components/common/sandbox/list-view/types'

interface AddItemProps {
  isEditing: boolean
  parentId: NullableId
  mode: AddMode
  /**
   * The amount of left margin to apply to the row according to its nesting level provided by the parent component (rows, to be precise)
   */
  marginLeft: number
  /**
   * Whether to show the create button or not
   */
  showCreateButton: boolean
}

// eslint-disable-next-line @typescript-eslint/no-empty-function
const EmptyFunction = () => {}

export const AddItemRow = observer((props: AddItemProps) => {
  const {isEditing, parentId, mode, showCreateButton, marginLeft} = props
  const store = useListStore()
  const [value, setValue] = React.useState('')

  const onSubmit = (value: string) => {
    setValue('')
    if (!value) {
      /**
       * Delay the turn off of the editor to prevent the editor from flickering when the user clicks the create button again
       */
      setTimeout(() => {
        // Turn off the editor if the value is empty
        store.turnOffAddItemEditor(parentId, mode)
      }, 100)
    }
    store.createItem(parentId, value, mode, true)
  }

  const enableEditor = () => {
    store.turnOnAddItemEditor(parentId, mode)
  }

  const temporaryItems = store.getTemporarilyCreatedItems(parentId, mode)

  const temporaryItemsContent = temporaryItems.map(item => (
    <LiteItemRow name={item.name} marginLeft={marginLeft} />
  ))

  const addItemContent =
    showCreateButton || isEditing ? (
      <TwRowContainer>
        <TwRowShell
          style={{
            marginLeft: `${marginLeft}px`
          }}
        >
          <TwCell>
            <TwInnerCell>
              {isEditing ? (
                <>
                  <TwCellFunctions>
                    <TwCellFunctionsCompensator />
                    <Tw.TwCellFunction>
                      <CheckboxNew
                        rounded
                        size="big"
                        color="green"
                        value={false}
                        onChange={EmptyFunction}
                      />
                    </Tw.TwCellFunction>
                  </TwCellFunctions>
                  <ItemName
                    value={value}
                    onValueChange={setValue}
                    isArchived={false}
                    isLazy={false}
                    color={null}
                    editable
                    isEditing
                    setEdit={flag => {
                      if (!flag) {
                        store.turnOffAddItemEditor(parentId, mode)
                      }
                    }}
                    onSubmitValue={onSubmit}
                    completed={false}
                    fromBackgroundColor={'from-background-hover'}
                    placeholder={Lang.msg.enterTaskName}
                    className="max-w-[530px]"
                  />
                </>
              ) : (
                <>
                  <TwCellFunctionsCompensator />
                  <LinkButton
                    icon="IconPlus"
                    noPaddings
                    size="small"
                    onClick={enableEditor}
                    className="ml-4"
                  >
                    {Lang.msg.taskPage.newTask}
                  </LinkButton>
                </>
              )}
            </TwInnerCell>
          </TwCell>
        </TwRowShell>
      </TwRowContainer>
    ) : null

  return (
    <>
      {mode === AddMode.BeforeUndoneTasks
        ? addItemContent
        : temporaryItemsContent}
      {mode === AddMode.BeforeUndoneTasks
        ? temporaryItemsContent
        : addItemContent}
    </>
  )
})
